import React, { useMemo, useState } from "react";
import { Container, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  PartnerBookingPolicy,
  PartnerBookingHour,
} from "../../../data/partners";
import { useI18n } from "../../../lib/i18n/i18n_provider";
import { MeetingRoomsFilter } from "../../../offering/template_offering_listing_meeting_room";
import styles from "./desktop_filters.module.css";
import { FilterId, MobileFilter } from "./mobile_filters";
import { FilterLocation, Partner } from "@/data/partner_offering";
import { LocationFilter } from "./location_filter";
import {
  useLocationDropdownFeatureFlag,
  useMrGalleryDateTimeFilterFeatureFlag,
  useCapacityFilterFeatureFlag,
} from "@/core/splitio";
import { DesktopFilterItem } from "./desktop_filters";
import { useLocationPickerState } from "../../hooks/use_location_picker_state";
import { useLocationParam } from "../../hooks/use_location_param";
import { uniqBy } from "lodash";
import { CapacityFilterV2 } from "./capacity_filter_v2";
import {
  DesktopDateTimeFilterItem,
  DesktopDateTimeFilterItemV2,
} from "./desktop_date_time_filter_item";

interface GalleryDesktopFiltersProps {
  partner: Partner;
  filters: MobileFilter;
  filterValues: MeetingRoomsFilter;
  locations: FilterLocation[];
  onFilterValuesChange: (filter: MeetingRoomsFilter) => void;
  partnerBookingPolicy?: PartnerBookingPolicy;
  partnerBookingHour?: PartnerBookingHour;
  maxCapacity: number;
  onClearFilters: () => void;
  onContactUs?: () => void;
}

export function GalleryDesktopFilters({
  partner,
  filters,
  filterValues,
  locations,
  onFilterValuesChange,
  partnerBookingHour,
  partnerBookingPolicy,
  onClearFilters,
  onContactUs,
  maxCapacity,
}: GalleryDesktopFiltersProps) {
  const { fbt } = useI18n();
  const locationDropdownFlag = useLocationDropdownFeatureFlag();
  const mrGalleryDateTimeFilterFlag = useMrGalleryDateTimeFilterFeatureFlag();
  const capacityFilterFlag = useCapacityFilterFeatureFlag();

  const { cities, locationIDs } = useLocationParam(locations);

  const [highlightLocation, setHighlightLocation] = useState(false);

  const {
    displayedValue,
    selectedCities,
    selectedLocationIDs,
    handleSelectCity,
    handleSelectLocation,
    handleClearSelection: handleClearLocationSelection,
  } = useLocationPickerState(locations, {
    cities,
    locationIDs,
  });

  const numberOfCities = uniqBy(locations, (l) => l.city).length;

  const locationOptions = useMemo(() => {
    let filteredLocations = filters.location?.options || [];
    if (filterValues.state) {
      filteredLocations = filteredLocations.filter(
        (o) => o.data.state === filterValues.state
      );
    }
    if (filterValues.city) {
      filteredLocations = filteredLocations.filter(
        (o) => o.data.city === filterValues.city
      );
    }
    return filteredLocations;
  }, [filters, filterValues]);

  const filterKeys = Object.keys(filters) as FilterId[];
  const numberOfFilters = filterKeys.length;

  if (numberOfFilters === 0) {
    return <></>;
  }

  const hasFilterValue = Object.values(filterValues).some((value) => !!value);

  const handleClearAllFilters = () => {
    onClearFilters();
    if (numberOfCities > 1) {
      handleClearLocationSelection();
    }
  };

  const disableDateTimeFilter =
    !selectedCities.length &&
    !selectedLocationIDs.length &&
    locations.length > 2 &&
    !!filters.location;

  return (
    <div className={`${styles.container} mr-gallery-desktop-filters`}>
      <Container className="d-flex align-items-center justify-content-between">
        <div className={styles.filtersWrapper}>
          {locationDropdownFlag && filters.location ? (
            <LocationFilter
              partner={partner}
              filters={filters}
              locations={locations}
              displayedValue={displayedValue}
              values={{
                cities: selectedCities,
                locationIDs: selectedLocationIDs,
              }}
              onClearSelection={handleClearLocationSelection}
              onSelectCity={handleSelectCity}
              onSelectLocation={handleSelectLocation}
              highlightBorder={highlightLocation}
            />
          ) : (
            <>
              {filters.state && (
                <DesktopFilterItem
                  iconName={filters.state.iconName}
                  value={filters.state.value}
                  onSelect={filters.state.onChange}
                  options={filters.state.options}
                  label={filters.state.label}
                  testID="state"
                />
              )}
              {filters.city && (
                <DesktopFilterItem
                  iconName={filters.city.iconName}
                  value={filters.city.value}
                  onSelect={filters.city.onChange}
                  options={filters.city.options}
                  label={filters.city.label}
                  testID="city"
                />
              )}
              {filters.location && (
                <DesktopFilterItem
                  iconName={filters.location.iconName}
                  value={filters.location.value}
                  onSelect={filters.location.onChange}
                  options={locationOptions}
                  placeholder={fbt("Any location")}
                  label={filters.location.label}
                  testID="location"
                />
              )}
            </>
          )}
          {filters.dateTime &&
            mrGalleryDateTimeFilterFlag &&
            (disableDateTimeFilter ? (
              <OverlayTrigger
                onToggle={(nextShow) => setHighlightLocation(nextShow)}
                overlay={
                  <Tooltip>
                    {fbt(
                      "Choose a city or location first to allow date and time filtering"
                    )}
                  </Tooltip>
                }
              >
                <div>
                  <DesktopDateTimeFilterItemV2
                    iconName={filters.dateTime.iconName}
                    value={filterValues}
                    onChange={onFilterValuesChange}
                    label={filters.dateTime.label}
                    openAt={partnerBookingHour?.openAt}
                    closedAt={partnerBookingHour?.closedAt}
                    timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
                    disabled
                  />
                </div>
              </OverlayTrigger>
            ) : (
              <DesktopDateTimeFilterItemV2
                iconName={filters.dateTime.iconName}
                value={filterValues}
                onChange={onFilterValuesChange}
                label={filters.dateTime.label}
                openAt={partnerBookingHour?.openAt}
                closedAt={partnerBookingHour?.closedAt}
                timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
              />
            ))}
          {filters.layout && (
            <DesktopFilterItem
              iconName={filters.layout.iconName}
              value={filters.layout.value}
              onSelect={filters.layout.onChange}
              options={filters.layout.options}
              label={filters.layout.label}
              testID="layout"
            />
          )}
          {filters.capacity && capacityFilterFlag ? (
            <div>
              <CapacityFilterV2
                value={
                  filters.capacity.value
                    ? Number(filters.capacity.value)
                    : undefined
                }
                maxCapacity={maxCapacity}
                onChange={(capacity) => {
                  if (filters.capacity && filters.capacity.onChange) {
                    filters.capacity.onChange(
                      capacity ? String(capacity) : undefined
                    );
                  }
                }}
                onContactUs={onContactUs}
                partner={partner}
              />
            </div>
          ) : (
            filters.capacity && (
              <DesktopFilterItem
                iconName={filters.capacity.iconName}
                value={filters.capacity.value}
                onSelect={filters.capacity.onChange}
                options={filters.capacity.options}
                label={filters.capacity.label}
                testID="capacity"
              />
            )
          )}
          {filters.dateTime && !mrGalleryDateTimeFilterFlag && (
            <DesktopDateTimeFilterItem
              iconName={filters.dateTime.iconName}
              value={filterValues}
              onChange={onFilterValuesChange}
              label={filters.dateTime.label}
              openAt={partnerBookingHour?.openAt}
              closedAt={partnerBookingHour?.closedAt}
              timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
            />
          )}
          {filters.productCategory && (
            <DesktopFilterItem
              iconName={filters.productCategory.iconName}
              value={filters.productCategory.value}
              onSelect={filters.productCategory.onChange}
              options={filters.productCategory.options}
              label={filters.productCategory.label}
              testID="productCategory"
            />
          )}
          {hasFilterValue && (
            <button
              className="btn btn-link fw-normal"
              style={{ color: "var(--bs-secondary)" }}
              onClick={handleClearAllFilters}
            >
              {fbt("Clear filters")}
            </button>
          )}
        </div>
      </Container>
    </div>
  );
}
